import React, { useEffect, useState } from 'react';
import { RobbieMark } from '../../ui';

/**
 * The Smart BOM reveal moment — shown once per version state at the top of
 * the supplier-discovery sub-step, just before the buyer meets suppliers:
 * "the BOM you're about to shop with is smarter than the one you uploaded."
 *
 * An inline banner-card, never a blocking overlay. This is AI-carrying
 * content, so it wears the Robbie surface (robbie-border + ai-panel, violet
 * primary action) like CorrectionBar and the "Robbie's picks" card — the one
 * licensed use of violet. Numbers come from the real version list
 * (useSmartBomReveal); purely presentational so the design gallery can
 * render it with sample data.
 */
export interface SmartBomRevealCardProps {
  /** Latest real version number, e.g. 4 for "it's now v4". */
  latestVersion: number;
  /** Real sum of recorded changes beyond the v1 "Extracted" baseline (≥1). */
  changeCount: number;
  /** Primary: open the Smart BOM sheet on the Reasoning story. */
  onSee: () => void;
  /** Quiet dismiss ("Not now") — the pill stays as the standing reference. */
  onDismiss: () => void;
  /** The criteria the research runs with — a compact `CriteriaBrief`, one line. */
  criteria?: React.ReactNode;
  className?: string;
}

export const SmartBomRevealCard: React.FC<SmartBomRevealCardProps> = ({
  latestVersion,
  changeCount,
  onSee,
  onDismiss,
  criteria,
  className = '',
}) => {
  // Gentle fade/slide on mount — plain transition utilities, no new deps.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const changesText = `${changeCount} change${changeCount === 1 ? '' : 's'}`;

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-s4 rounded-card border border-robbie-border bg-ai-panel
        px-s6 py-s4 transition-all duration-500 ease-out motion-safe:transform
        ${entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'} ${className}`}
    >
      <RobbieMark size="sm" state="insight" aria-label="Robbie has an insight" />
      <div className="min-w-0 flex-1 max-md:basis-[calc(100%-36px)]">
        <p className="m-0 text-row-title text-ink">Your BOM is smarter than the one you uploaded.</p>
        <p className="m-0 mt-[2px] text-body-sm text-ink-body">
          I've been improving it as we went — it's now{' '}
          <span className="font-mono text-mono-data">v{latestVersion}</span>, {changesText} smarter
          than your file. Want to see what changed before we pick suppliers?
        </p>
        {criteria && <div className="mt-s2">{criteria}</div>}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-s3 max-md:ml-0 max-md:w-full max-md:justify-end">
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex h-7 items-center whitespace-nowrap rounded-control px-s3 text-body-sm
            font-medium text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink
            focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={onSee}
          className="inline-flex h-7 items-center whitespace-nowrap rounded-control bg-robbie px-s4
            text-body-sm font-semibold text-white transition-opacity hover:opacity-90
            focus:outline-none focus-visible:ring-2 focus-visible:ring-robbie focus-visible:ring-offset-1"
        >
          See the smarter BOM
        </button>
      </div>
    </div>
  );
};

export default SmartBomRevealCard;
